import React, { useState, useRef, useEffect } from 'react';

// 🍿 Watch Party tab — the launcher/queue/history surface (Stage spec D1).
// The theater itself stays on the stage above chat (GameStepPage); this panel is
// where you start a party, build the "what's next" playlist, and browse the
// space's viewing history. Starts/queues go through the same `watchparty:request`
// window event the media cards use, so this component stays dependency-light.
// Spec: STAGE_DOCK_SPEC_2026_07_11.md + WATCH_PARTY_SPEC_2026_07_10.md

const YT_URL_RE = /(?:youtube\.com\/(?:watch\?.*v=|shorts\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/;

const isYouTubeUrl = (s) => YT_URL_RE.test(String(s || '').trim());

const timeAgo = (iso) => {
  if (!iso) return '';
  const mins = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return days < 7 ? `${days}d ago` : new Date(iso).toLocaleDateString();
};

const WatchPartyLauncher = ({
  watchParty,        // active party state or null
  history,           // watch_party_history array (oldest first) or null
  mediaSuggestions,  // chatGameStatus.media_suggestions
  pinnedPick,        // host's pinned pick (may be a youtube item)
  inTheater,         // whether this user is seated
  onTakeSeat,        // () => enter theater
  humanPlayerId,     // null when spectating
  THEME,
  isMobile,
  // 📱 TV place chrome (places nav v3, Nina 08-01): the now-playing card
  // becomes the MERGED card — "ON THE TV" purple header (no emoji), 🍿 stays
  // on the eyebrow, emoji-free CTAs, and queueing goes front and center
  // (+ Queue beside the seat, + Add on the queue header — both jump to the
  // picker below). Desktop and the stage keep the classic layout.
  tvChrome = false,
  // TV place with the AMBIENT pick playing above: the idle "Nothing on right
  // now" hero would contradict a playing screen — the page hides it; the
  // paste bar + suggestion rail below still render (they're the queue path).
  hideIdleHero = false,
  // Desktop idle-stage mount (Nina 08-12): the "🎭 On stage" header row is
  // gone, so the collapse-to-one-line-bar ⌄ rides the paste bar instead.
  onCollapse = null,
}) => {
  const [urlInput, setUrlInput] = useState('');
  const [flash, setFlash] = useState(null);
  const flashTimer = useRef(null);
  const addBarRef = useRef(null);
  const addInputRef = useRef(null);
  useEffect(() => () => clearTimeout(flashTimer.current), []);

  const jumpToAdd = () => {
    // Scroll ONLY — the old auto-focus popped the keyboard, and the keyboard
    // hides the bottom nav (typingFocus), which read as the tabs breaking
    // (Nina 08-02). One more tap to type beats a surprise keyboard.
    try { addBarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { /* anchor gone */ }
  };

  const partyLive = !!watchParty;
  const queue = watchParty?.queue || [];

  const showFlash = (text) => {
    clearTimeout(flashTimer.current);
    setFlash(text);
    flashTimer.current = setTimeout(() => setFlash(null), 2600);
  };
  // 🍿🚫 09-22: the server's refusal ("The projector is locked tonight.",
  // "A watch party is already running", "That video can't be embedded") lands
  // here, replacing the optimistic "Lights down!" flash above. GameStepPage
  // dispatches it from the request path; every mount listens, only the
  // visible one is seen. Refs + the state setter only: no stale closure.
  useEffect(() => {
    const onRefused = (e) => {
      const msg = e && e.detail && e.detail.message;
      if (!msg) return;
      clearTimeout(flashTimer.current);
      setFlash(String(msg).slice(0, 120));
      flashTimer.current = setTimeout(() => setFlash(null), 4200);
    };
    window.addEventListener('watchparty:refused', onRefused);
    return () => window.removeEventListener('watchparty:refused', onRefused);
  }, []);

  const requestParty = (url) => {
    if (!humanPlayerId) {
      showFlash('Take a seat in the space first! Join to start watching.');
      return;
    }
    window.dispatchEvent(new CustomEvent('watchparty:request', { detail: { url } }));
    showFlash(partyLive ? 'Added to what’s next!' : 'Lights down! Starting the party.');
  };

  const submitUrl = () => {
    const url = urlInput.trim();
    if (!isYouTubeUrl(url)) {
      showFlash('That needs to be a YouTube link.');
      return;
    }
    requestParty(url);
    setUrlInput('');
  };

  // YouTube-playable suggestions: host's pinned pick first (when it's a video),
  // then the media-tab youtube rail, deduped by url.
  const ytSuggestions = (() => {
    const items = [];
    if (pinnedPick?.type === 'youtube' && pinnedPick.url) {
      items.push({ ...pinnedPick, _hostPick: true });
    }
    (mediaSuggestions?.youtube || []).forEach(item => {
      if (item?.url && !items.some(x => x.url === item.url)) items.push(item);
    });
    return items.slice(0, 8);
  })();

  const pastShows = Array.isArray(history) ? [...history].reverse().slice(0, 12) : [];

  const accent = THEME?.accent || '#7b1fa2';
  // Text-role inks (Nina 08-03: TV tab was unreadable on brown themes).
  // textMuted arrives verbatim from the AI theme and regularly lands ~3:1 on
  // bgLight; accent can match the panel's lightness and vanish. Fills and
  // gradients keep the raw accent — only TEXT swaps to the derived tokens.
  const accentInk = THEME?.accentOnPanel || accent;
  const panelDark = (THEME?.textOnPanel || '#ffffff') === '#ffffff';
  const mutedInk = panelDark ? 'rgba(255,255,255,0.72)' : 'rgba(0,0,0,0.62)';
  const card = {
    backgroundColor: THEME?.bgLight || 'rgba(255,255,255,0.05)',
    border: `1px solid ${THEME?.borderLight || '#444'}40`,
    borderRadius: 12,
  };

  const primaryBtn = (extra = {}) => ({
    border: 'none', cursor: 'pointer', color: '#fff', fontWeight: 700,
    padding: '8px 16px', borderRadius: 999, fontSize: '0.85em',
    background: `linear-gradient(135deg, ${accent}, #4a0e6b)`,
    boxShadow: '0 2px 10px rgba(0,0,0,0.35)',
    ...extra,
  });

  const SuggestionCard = ({ item }) => (
    <div style={{ ...card, flexShrink: 0, width: isMobile ? 190 : 220, overflow: 'hidden', position: 'relative' }}>
      <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%', backgroundColor: '#000' }}>
        {item.thumbnail ? (
          <img src={item.thumbnail} alt="" loading="lazy"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => { e.target.style.display = 'none'; }} />
        ) : (
          <div style={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)', fontSize: '2em',
          }}>▶️</div>
        )}
        {item._hostPick && (
          <span style={{
            position: 'absolute', top: 6, left: 6, fontSize: '0.6em', fontWeight: 800,
            padding: '3px 8px', borderRadius: 8, backgroundColor: '#ec407a', color: '#fff',
            textTransform: 'uppercase', letterSpacing: '0.5px',
            // 🎞️ F1 (09-26): a ported room's pick is the resident's
            // (utils/portedPick: pick_label + his round face); else as before
            ...(item.pick_label ? { display: 'inline-flex', alignItems: 'center', gap: 4 } : {}),
          }}>{item.pick_label ? (
            <>
              {item.by_image && (
                <img src={item.by_image} alt="" data-pick-face=""
                  style={{ width: 14, height: 14, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                  onError={(e) => { e.target.style.display = 'none'; }} />
              )}
              {item.pick_label}
            </>
          ) : <>{item.host_emoji || '🎙️'} Host&rsquo;s pick</>}</span>
        )}
      </div>
      <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{
          fontSize: '0.78em', fontWeight: 600, color: THEME?.textBright || '#fff',
          lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          minHeight: '2.6em',
        }}>{item.title || 'Untitled'}</div>
        <button onClick={() => requestParty(item.url)} style={primaryBtn({ alignSelf: 'flex-start', padding: '6px 12px', fontSize: '0.75em' })}>
          {partyLive ? '➕ Queue it' : '🍿 Watch together'}
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, padding: '4px 2px 20px' }}>
      <style>{`.wp-launcher-rail::-webkit-scrollbar { display: none; }`}</style>

      {/* Transient feedback toast */}
      {flash && (
        <div style={{
          position: 'sticky', top: 4, zIndex: 5, alignSelf: 'center',
          padding: '8px 16px', borderRadius: 999, fontSize: '0.8em', fontWeight: 700, color: '#fff',
          background: 'linear-gradient(135deg, rgba(30,10,45,0.97), rgba(70,20,110,0.97))',
          border: `1px solid ${accent}90`, boxShadow: '0 6px 20px rgba(0,0,0,0.45)',
          animation: 'fadeIn 0.2s ease',
        }}>{flash}</div>
      )}

      {partyLive ? (
        <>
          {/* NOW PLAYING — jump back to the couch. Under tvChrome this is THE
              merged card: the old floating purple bar became this header. */}
          <div style={{ ...card, overflow: 'hidden', ...(tvChrome ? { border: `1px solid ${accent}70` } : {}) }}>
            {tvChrome && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px',
                background: `linear-gradient(90deg, ${accent}55, rgba(40,0,60,0.5))`,
                borderBottom: `1px solid ${accent}50`, fontSize: '0.68em', color: '#fff',
              }}>
                <span style={{ fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase' }}>On the TV</span>
                {watchParty.started_by_name && (
                  <span style={{ marginLeft: 'auto', opacity: 0.75, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {watchParty.host_mode ? `${watchParty.started_by_name}'s programming` : `started by ${watchParty.started_by_name}`}
                  </span>
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: 14, padding: 14, alignItems: 'center', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
              <div style={{ position: 'relative', flexShrink: 0, width: isMobile ? '100%' : 200, borderRadius: 8, overflow: 'hidden', backgroundColor: '#000' }}>
                <div style={{ width: '100%', paddingTop: '56.25%' }} />
                {watchParty.thumbnail && (
                  <img src={watchParty.thumbnail} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                )}
                <span style={{
                  position: 'absolute', bottom: 6, left: 6, fontSize: '0.62em', fontWeight: 800,
                  padding: '3px 8px', borderRadius: 8, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.5px',
                  backgroundColor: watchParty.state === 'playing' ? '#16a34a' : '#6b7280',
                }}>{watchParty.state === 'playing' ? '▶ Playing' : '⏸ Paused'}</span>
              </div>
              <div style={{ flex: 1, minWidth: 180, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: '0.65em', fontWeight: 800, letterSpacing: '1.5px', textTransform: 'uppercase', color: accentInk }}>
                  🍿 Now playing
                </div>
                <div style={{ fontSize: '1em', fontWeight: 700, color: THEME?.textBright || '#fff', lineHeight: 1.3 }}>
                  {watchParty.title}
                </div>
                {/* tvChrome: the header already names the programmer — keep
                    only the steer nudge; classic keeps the full line */}
                {watchParty.started_by_name && !tvChrome && (
                  <div style={{ fontSize: '0.75em', color: mutedInk }}>
                    {watchParty.host_mode
                      ? <>📺 {watchParty.started_by_name}&rsquo;s programming. Queue something to steer!</>
                      : <>Started by {watchParty.started_by_name}</>}
                  </div>
                )}
                {tvChrome && watchParty.host_mode && (
                  <div style={{ fontSize: '0.75em', color: mutedInk }}>
                    Queue something to steer!
                  </div>
                )}
                {inTheater && (
                  <div style={{ fontSize: '0.75em', color: mutedInk }}>
                    You&rsquo;re on the couch! The show is up on the stage.
                  </div>
                )}
                {/* One queue verb only (Nina 08-01): WHAT'S NEXT owns + Add,
                    so the card keeps a single full-width seat button */}
                {!inTheater && (
                  <button
                    onClick={onTakeSeat}
                    style={primaryBtn(tvChrome
                      ? { width: '100%', letterSpacing: '0.04em' }
                      : { alignSelf: 'flex-start' })}
                  >
                    {tvChrome ? 'Take a seat' : '🛋️ Take a seat'}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* WHAT'S NEXT — the shared playlist */}
          <div>
            <div style={{ fontSize: '0.8em', fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase', color: mutedInk, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
              {tvChrome ? 'What’s next' : '⏭️ What’s next'} {queue.length > 0 && <span style={{ color: accentInk }}>({queue.length})</span>}
              {tvChrome && (
                <button
                  onClick={jumpToAdd}
                  style={{
                    marginLeft: 'auto', cursor: 'pointer', fontSize: '0.85em', fontWeight: 800,
                    padding: '3px 12px', borderRadius: 999, background: 'transparent',
                    border: `1px solid ${accent}70`, color: THEME?.textBright || '#fff',
                    letterSpacing: '0.03em', textTransform: 'none',
                  }}
                >+ Add</button>
              )}
            </div>
            {queue.length === 0 ? (
              <div style={{ ...card, padding: '14px 16px', fontSize: '0.8em', color: mutedInk }}>
                Nothing queued yet. Add a video below and keep the party rolling!
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {queue.map((q, i) => (
                  <div key={`${q.video_id}-${i}`} style={{ ...card, display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px' }}>
                    <span style={{ fontSize: '0.8em', fontWeight: 800, color: accentInk, width: 18, textAlign: 'center', flexShrink: 0 }}>{i + 1}</span>
                    {q.thumbnail && (
                      <img src={q.thumbnail} alt="" style={{ width: 64, height: 36, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.8em', fontWeight: 600, color: THEME?.textBright || '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {q.title || q.url}
                      </div>
                      {q.added_by_name && (
                        <div style={{ fontSize: '0.68em', color: mutedInk }}>added by {q.added_by_name}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      ) : hideIdleHero ? null : (
        /* IDLE — the launcher hero. tvChrome = the DARK SCREEN (launcher-first,
           Nina 07-31): same card wearing the ON THE TV header, CTA on the glass. */
        <div style={{ ...card, overflow: 'hidden', ...(tvChrome ? { border: `1px solid ${accent}70` } : {}) }}>
          {tvChrome && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px',
              background: `linear-gradient(90deg, ${accent}55, rgba(40,0,60,0.5))`,
              borderBottom: `1px solid ${accent}50`, fontSize: '0.68em', color: '#fff',
            }}>
              <span style={{ fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase' }}>On the TV</span>
            </div>
          )}
          <div style={{
            padding: isMobile ? '22px 16px' : '28px 24px', textAlign: 'center',
            background: `linear-gradient(160deg, ${THEME?.bgLight || 'rgba(255,255,255,0.05)'}, rgba(70,20,110,0.25))`,
          }}>
            <div style={{ fontSize: '2.4em', marginBottom: 6 }}>🍿</div>
            <div style={{ fontSize: '1.05em', fontWeight: 800, color: THEME?.textBright || '#fff', marginBottom: 6 }}>
              {tvChrome ? 'Nothing on right now' : 'Start a watch party'}
            </div>
            <div style={{ fontSize: '0.8em', color: mutedInk, maxWidth: 420, margin: '0 auto', lineHeight: 1.45 }}>
              Synced video with everyone in the room. The locals watch too!
            </div>
            {tvChrome && (
              <button onClick={jumpToAdd} style={primaryBtn({ marginTop: 12, letterSpacing: '0.04em' })}>
                Put something on
              </button>
            )}
          </div>
        </div>
      )}

      {/* Paste-a-link bar — start when idle, queue when live. The tvChrome
          + Queue / + Add / Put something on buttons all land here. */}
      <div ref={addBarRef} style={{ display: 'flex', gap: 8 }}>
        <input
          ref={addInputRef}
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submitUrl(); }}
          placeholder="Paste a YouTube link…"
          style={{
            flex: 1, minWidth: 0, padding: '10px 14px', borderRadius: 999, fontSize: '0.85em',
            border: `1px solid ${THEME?.borderLight || '#444'}60`,
            backgroundColor: THEME?.bgMedium || 'rgba(0,0,0,0.3)',
            color: THEME?.textBright || '#fff', outline: 'none',
          }}
        />
        <button onClick={submitUrl} style={primaryBtn({ flexShrink: 0 })}>
          {tvChrome ? (partyLive ? 'Queue' : 'Start') : (partyLive ? '➕ Queue' : '🍿 Start')}
        </button>
        {onCollapse && (
          <button
            onClick={onCollapse}
            aria-label="Collapse"
            title="Back to the one-line bar"
            style={{
              flexShrink: 0, cursor: 'pointer', fontWeight: 700, borderRadius: 999,
              fontSize: '1em', lineHeight: 1, padding: '5px 12px', color: '#fff',
              border: '1.5px solid rgba(255,255,255,0.5)', backgroundColor: 'rgba(0,0,0,0.35)',
            }}
          >
            ⌄
          </button>
        )}
      </div>

      {/* Suggestion rail — the host has taste */}
      {ytSuggestions.length > 0 && (
        <div>
          <div style={{ fontSize: '0.8em', fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase', color: mutedInk, marginBottom: 10 }}>
            🎬 {partyLive ? 'Add something good' : 'Something good to watch'}
          </div>
          <div className="wp-launcher-rail" style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 4, scrollbarWidth: 'none' }}>
            {ytSuggestions.map((item, i) => <SuggestionCard key={item.url || i} item={item} />)}
          </div>
        </div>
      )}

      {/* Previously watched — ticket stubs from past parties */}
      {pastShows.length > 0 && (
        <div>
          <div style={{ fontSize: '0.8em', fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase', color: mutedInk, marginBottom: 10 }}>
            🎟️ Previously watched
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {pastShows.map((h, i) => (
              <div key={`${h.video_id}-${h.ended_at}-${i}`} style={{
                ...card, display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                borderLeft: `3px dashed ${accent}70`,
              }}>
                <span style={{ fontSize: '1.1em', flexShrink: 0 }}>🎟️</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.8em', fontWeight: 600, color: THEME?.textBright || '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {h.title || 'Untitled'}
                  </div>
                  <div style={{ fontSize: '0.68em', color: mutedInk }}>
                    {h.started_by_name ? `${h.started_by_name} · ` : ''}{timeAgo(h.ended_at || h.started_at)}
                  </div>
                </div>
                {h.video_id && (
                  <button
                    onClick={() => requestParty(`https://www.youtube.com/watch?v=${h.video_id}`)}
                    title={partyLive ? 'Queue this again' : 'Watch this again'}
                    style={{
                      flexShrink: 0, cursor: 'pointer', fontSize: '0.72em', fontWeight: 700,
                      padding: '5px 12px', borderRadius: 999, color: THEME?.textBright || '#fff',
                      border: `1px solid ${accent}80`, backgroundColor: 'transparent',
                    }}
                  >↻ Rewatch</button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(WatchPartyLauncher);
